/**
 * Tam ekran "Şimdi Çalıyor" yüzeyi (Tasarım V4, Öneri 7).
 *
 * Mini oynatıcıdan açılır; albüm kapağı, ilerleme, oynat/duraklat, sonraki,
 * ses ve hız kontrollerini tek yerde toplar. Rota değildir: müzik çalarken
 * herhangi bir ekranın üzerine açılır ve kapanınca müzik devam eder.
 */
import type { ReactNode } from 'react';
import { formatDuration } from '../format.ts';
import {
  ChevronDownIcon,
  MusicIcon,
  PauseIcon,
  PlayIcon,
  QueueIcon,
  SkipNextIcon,
  VolumeIcon,
} from '../Icon.tsx';
import { resizeProxyImage } from '../imageSize.ts';
import { mediaCopy } from '../mediaCopy.ts';
import { mediaUrl } from '../mediaToken.ts';
import { monogramGradient } from '../monogram.ts';
import { PLAYER_RATES } from '../playerPrefs.ts';
import type { AudioPlayerApi } from '../useAudioPlayer.ts';

export function NowPlaying({
  player,
  onClose,
  onOpenQueue,
}: {
  player: AudioPlayerApi;
  onClose: () => void;
  onOpenQueue: () => void;
}): ReactNode {
  const { current } = player;
  if (current === null) return null;
  const copy = mediaCopy('music');

  const volumeLevel =
    player.muted || player.volume === 0 ? 'muted' : player.volume < 0.5 ? 'low' : 'high';

  /*
   * Albüm kapağı parçanın gömülü kapağından önce gelir; albüm kapağı varsa
   * yer tutucu nota gizlenir.
   */
  const albumArtwork =
    current.albumCoverUrl ??
    resizeProxyImage(current.posterUrl, 'w500') ??
    current.thumbnailUrl ??
    null;
  const artwork = albumArtwork ?? mediaUrl(`/api/items/${current.id}/thumbnail`);

  return (
    <div className="now-playing" role="dialog" aria-modal="true" aria-label="Şimdi çalıyor">
      <button
        type="button"
        className="now-playing__backdrop"
        onClick={onClose}
        aria-label="Kapat"
        tabIndex={-1}
      />

      <div className="now-playing__panel">
        <button
          type="button"
          className="now-playing__close"
          onClick={onClose}
          aria-label="Şimdi çalıyor ekranını kapat"
          title="Küçült"
        >
          <ChevronDownIcon size={20} />
        </button>

        <div
          className="now-playing__cover"
          style={{
            backgroundImage: `url(${artwork}), ${monogramGradient(current.id)}`,
          }}
          aria-hidden="true"
        >
          {albumArtwork === null && <MusicIcon size={48} />}
        </div>

        <div className="now-playing__meta">
          <h2 className="now-playing__title">{current.title}</h2>
          <p className="now-playing__sub">
            {current.year ?? 'pbfork'} · {formatDuration(player.durationMs)}
          </p>
        </div>

        <div className="now-playing__seek">
          <input
            type="range"
            className="now-playing__range"
            min={0}
            max={Math.max(player.durationMs, 1)}
            step={1000}
            value={Math.min(player.positionMs, Math.max(player.durationMs, 1))}
            aria-label="Parça konumu"
            onChange={(event) => player.seekTo(Number(event.target.value))}
          />
          <div className="now-playing__times">
            <span>{formatDuration(player.positionMs)}</span>
            <span>{formatDuration(player.durationMs)}</span>
          </div>
        </div>

        <div className="now-playing__controls">
          <button
            type="button"
            className="now-playing__play"
            onClick={player.toggle}
            aria-label={player.isPlaying ? 'Duraklat' : copy.play}
            title={player.isPlaying ? 'Duraklat' : copy.play}
          >
            {player.isPlaying ? <PauseIcon size={26} /> : <PlayIcon size={26} />}
          </button>
          <button
            type="button"
            className="now-playing__next"
            onClick={player.next}
            aria-label="Sonraki parça"
            title="Sonraki parça"
          >
            <SkipNextIcon size={22} />
          </button>
          <button
            type="button"
            className="now-playing__next"
            onClick={onOpenQueue}
            aria-label="Çalma sırasını aç"
            title="Çalma sırası"
          >
            <QueueIcon size={22} />
          </button>
        </div>

        <div className="now-playing__settings">
          <div className="now-playing__volume">
            <button
              type="button"
              className="now-playing__icon-btn"
              onClick={player.toggleMute}
              aria-label={player.muted ? 'Sesi aç' : 'Sesi kapat'}
              title={player.muted ? 'Sesi aç' : 'Sesi kapat'}
            >
              <VolumeIcon level={volumeLevel} size={16} />
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={player.muted ? 0 : player.volume}
              aria-label="Ses düzeyi"
              onChange={(event) => player.setVolume(Number(event.target.value))}
            />
          </div>

          <label className="now-playing__rate">
            <span className="visually-hidden">Çalma hızı</span>
            <select
              value={String(player.rate)}
              onChange={(event) => player.setRate(Number(event.target.value))}
              aria-label="Çalma hızı"
            >
              {PLAYER_RATES.map((rate) => (
                <option key={rate} value={String(rate)}>
                  {rate === 1 ? '1×' : `${rate}×`}
                </option>
              ))}
            </select>
          </label>
        </div>

        {player.error !== null && (
          <p className="now-playing__error" role="status">
            {player.error}
          </p>
        )}
      </div>
    </div>
  );
}
